import React, { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import './Chooseobs.css'
import useCountUp from '../hooks/useCountUp'

import {
  FaBolt,
  FaShoppingCart,
  FaLock,
  FaBoxOpen,
  FaStar,
  FaBell,
  FaUsers,
  FaCoins,
  FaCheckCircle,
} from 'react-icons/fa'

import bg2 from '../images/chooseobc/bg2.png'
import bg3 from '../images/chooseobc/bg3.png'
import bg4 from '../images/chooseobc/bg4.png'
import bg5 from '../images/chooseobc/bg5.png'
import bg6 from '../images/chooseobc/bg6.png'

import card6 from '../images/chooseobc/card6.png'
import cards1 from '../images/chooseobc/cards1.png'
import cards2 from '../images/chooseobc/cards2.png'
import cards5 from '../images/chooseobc/cards5.png'

const chooseCards = [
  {
    id: 1,
    bg: bg2,
    image: cards1,
    icon: FaBolt,
    titleTop: 'Live',
    titleBottom: 'Auctions',
    text: 'High-energy timed auctions with real competition and anti-sniping protection.',
    button: 'Bid Now',
    auctionEndsAt: Date.now() + (2 * 3600 + 14 * 60 + 36) * 1000,
    variant: 'wide',
  },
  {
    id: 2,
    bg: bg2,
    image: cards2,
    icon: FaShoppingCart,
    titleTop: 'Buy Now',
    titleBottom: 'Shop',
    text: 'Fixed-price cards, memorabilia and exclusive drops. Add to your weekly bundle.',
    button: 'Shop Now',
    variant: 'wide',
  },
  {
    id: 3,
    bg: bg3,
    image: null,
    icon: FaLock,
    titleTop: 'The',
    titleBottom: 'Vault',
    text: 'Your personal hub for wins, orders, watchlist, and bundle tracking.',
    button: 'Go To Vault',
    variant: 'short',
  },
  {
    id: 4,
    bg: bg4,
    image: null,
    icon: FaBoxOpen,
    titleTop: 'Weekly',
    titleBottom: 'Bundle',
    text: 'Win all week. Ship once. Combine your items and save on shipping.',
    button: 'How It Works',
    variant: 'wide',
  },
  {
    id: 5,
    bg: bg5,
    image: cards5,
    icon: FaStar,
    titleTop: 'Consign',
    titleBottom: 'With OBC',
    text: 'Turn your grails into opportunities. We list it, you get paid.',
    button: 'Learn More',
    variant: 'wide',
  },
  {
    id: 6,
    bg: bg6,
    image: card6,
    icon: FaBell,
    titleTop: 'Real Time',
    titleBottom: 'Alerts',
    text: 'Never miss a beat. Get notified for outbids, wins, new drops and ending soon.',
    button: 'Notify Me',
    variant: 'short',
  },
]

const stats = [
  {
    icon: FaUsers,
    number: 10,
    suffix: 'K+',
    text: 'Active collectors in the arena.',
  },
  {
    icon: FaCoins,
    number: 25,
    suffix: 'K+',
    text: 'Auctions completed.',
  },
  {
    icon: FaCheckCircle,
    number: 100,
    suffix: '%',
    text: 'Verified bidders secure payments.',
  },
]

function AuctionTimer({ endsAt }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, endsAt - Date.now()))

  useEffect(() => {
    const timer = setInterval(() => {
      setRemaining(Math.max(0, endsAt - Date.now()))
    }, 1000)

    return () => clearInterval(timer)
  }, [endsAt])

  const totalSeconds = Math.floor(remaining / 1000)
  const pad = (value) => String(value).padStart(2, '0')

  const hours = pad(Math.floor(totalSeconds / 3600))
  const minutes = pad(Math.floor((totalSeconds % 3600) / 60))
  const seconds = pad(totalSeconds % 60)

  return (
    // 
    <div className=""></div>
  )
}

function ChooseCard({ card }) {
  const Icon = card.icon

  const cardClassName = [
    'obc-card',
    card.variant ? `obc-card--${card.variant}` : '',
    card.id === 1 || card.id === 2 ? 'obc-card--compact' : '',
    card.id === 5 ? 'obc-card--featured' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={cardClassName} style={{ backgroundImage: `url(${card.bg})` }}>
      <div className="obc-card__scrim" />

      <div className="obc-card__content">
        <div className="obc-card__icon">
          <Icon />
        </div>

        <h3 className="obc-card__title">
          <span>{card.titleTop}</span>
          <strong>{card.titleBottom}</strong>
        </h3>

        <div className="obc-card__divider" />

        <p className="obc-card__text">{card.text}</p>

        <button type="button" className="obc-card__btn">
          {card.button}
        </button>
      </div>

      {card.image && (
        <div className="obc-card__media">
          <img src={card.image} alt={card.titleBottom} className="obc-card__media-img" />

          {card.rating && <span className="obc-card__rating">{card.rating}</span>}

          {card.auctionEndsAt && <AuctionTimer endsAt={card.auctionEndsAt} />}
        </div>
      )}
    </article>
  )
}

function StatItem({ item }) {
  const Icon = item.icon
  const [count, ref] = useCountUp(item.number)

  return (
    <div className="chooseobc-stat" ref={ref}>
      <div className="chooseobc-stat-icon">
        <Icon />
      </div>

      <div>
        <h4>
          {count}
          {item.suffix}
        </h4>
        <p>{item.text}</p>
      </div>
    </div>
  )
}

function Chooseobs() {
  const sectionRef = useRef(null)
  const headerRef = useRef(null)
  const gridRef = useRef(null)
  const statsRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

      tl.from(headerRef.current, {
        opacity: 0,
        y: 24,
        duration: 0.7,
      })
        .from(
          gridRef.current.children,
          {
            opacity: 0,
            y: 34,
            scale: 0.98,
            duration: 0.7,
            stagger: 0.09,
          },
          '-=0.2'
        )
        .from(
          statsRef.current.children,
          {
            opacity: 0,
            y: 18,
            duration: 0.55,
            stagger: 0.08,
          },
          '-=0.25'
        )
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section className="chooseobc-section" ref={sectionRef}>
      <div className="chooseobc-container">
        <div className="chooseobc-header" ref={headerRef}>
          <span className="chooseobc-eyebrow">Why Collectors Choose OBC</span>

          <h2 className="chooseobc-title">
            Built Different, Built
            <br />
            For Collectors
          </h2>

          <p className="chooseobc-text">
            OBC Cards &amp; Collectibles isn&apos;t just another marketplace. It&apos;s an arena
            where collectors compete, win and level up.
          </p>
        </div>

        <div className="chooseobc-grid" ref={gridRef}>
          {chooseCards.map((card) => (
            <ChooseCard key={card.id} card={card} />
          ))}
        </div>

        <div className="chooseobc-stats" ref={statsRef}>
          {stats.map((item, index) => (
            <StatItem item={item} key={index} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Chooseobs